import { c as _c } from "react/compiler-runtime";
import { HotelCardLinkProperties } from "@website/accommodation-ui/hotel-card-new/hotel-card-link/hotel-card-link.types";
import { HotelCardPropertiesType, HotelInfo } from "@website/accommodation-ui/hotel-card-new/hotel-card.types";
import { useCompatRouter } from "@website/hooks/compat-router";
import { setTracking } from "@website/store/tracking";
import { addEventToDataLayer } from "@website/tracking/tracking.gtm";
import { DATA_LAYER_EVENTS, USER_TRACKING_EVENTS } from "@website/tracking/tracking.types";
import { ApplicationNameType, addEventToTracking, mapProductForUserTracking } from "@website/tracking/user";
import omit from "lodash-es/omit";
import toNumber from "lodash-es/toNumber";
import Link from "next/link";
import { NextRouter } from "next/router";
import { Dispatch, SetStateAction } from "react";
export const hotelCardOnClick = ({
  hotelInfo,
  index,
  router,
  setClickLoading,
  onLinkClick,
  query,
  hash,
  homePageSectionName,
  isHomePage,
  isLastMinute
}: {
  hotelInfo?: HotelInfo;
  index: HotelCardPropertiesType["index"];
  router: NextRouter | any;
  setClickLoading: Dispatch<SetStateAction<number>>;
  query?: object;
  hash?: string;
  homePageSectionName?: string;
  isLastMinute?: boolean;
  isHomePage?: boolean;
} & Pick<HotelCardLinkProperties, "onLinkClick">) => {
  if (!hotelInfo) return () => undefined;
  return (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    const isPricingClick = Boolean((event.target as HTMLElement).closest(".hotel-card-pricing"));
    const isTitleClick = Boolean((event.target as HTMLElement).closest(".hotel-card-title"));
    const isImageClick = Boolean((event.target as HTMLElement).closest(".hotel-card-image"));
    setClickLoading(hotelInfo?.id);
    setTracking(previous => ({
      ...previous,
      hotelPriceEngineRuleId: toNumber(hotelInfo.ruleId)
    }));
    addEventToDataLayer(DATA_LAYER_EVENTS.SELECT_ITEM, {
      items: [{
        item_name: hotelInfo?.hotelDetail?.hotelTitle,
        item_id: hotelInfo?.id,
        price: hotelInfo.pricing.price,
        discount: hotelInfo.pricing.price - (hotelInfo.pricing.priceOff ?? 0),
        item_brand: process.env.NEXT_PUBLIC_APP_NAME === "hotel-international" ? "IH_hotel" : "IRR",
        item_category: hotelInfo?.hotelDetail?.cityTitle,
        item_category2: hotelInfo?.hotelDetail?.accommodationType,
        item_category3: hotelInfo?.hotelDetail?.stars,
        item_list_name: `هتل های ${hotelInfo?.hotelDetail?.cityTitle}`,
        item_list_id: hotelInfo?.hotelDetail?.cityId,
        ranking: index
      }]
    });
    if (isLastMinute) {
      addEventToTracking(USER_TRACKING_EVENTS.HOTEL_HOME_LAST_MINUTE_CLICKED, {
        product: "dom-hotel",
        hotel: hotelInfo?.hotelDetail?.hotelTitle,
        city: hotelInfo?.hotelDetail?.cityTitle,
        price: hotelInfo.pricing.price,
        position: index
      });
    }
    if ((homePageSectionName || isHomePage) && !isLastMinute) {
      const payload = {
        property_id: hotelInfo?.hotelDetail?.propertyId || 0,
        property_name: hotelInfo?.hotelDetail?.hotelTitle,
        city: hotelInfo?.hotelDetail.cityTitle,
        price: hotelInfo.pricing.price,
        position: index,
        section_name: homePageSectionName
      };
      addEventToTracking(USER_TRACKING_EVENTS.VILLA_HOME_PROPERTY_CARD_CLICKED, {
        ...payload,
        product: mapProductForUserTracking("villa")
      });
    }
    if (["villa", "hotel-domestic", "tour", "hotel-international"].includes(process.env.NEXT_PUBLIC_APP_NAME as ApplicationNameType)) addEventToTracking(USER_TRACKING_EVENTS.HOTEL_RESERVE_BUTTON_CLICKED, {
      ranking: index,
      date_from: String(router.query.date_from) || "",
      date_to: String(router.query.date_to) || "",
      city: hotelInfo?.hotelDetail.cityTitle,
      city_id: hotelInfo?.hotelDetail.cityId,
      hotel: hotelInfo?.hotelDetail?.hotelTitle,
      hotel_id: hotelInfo.id,
      price: hotelInfo.pricing.price,
      price_off: hotelInfo.pricing.priceOff,
      discount_percent: hotelInfo.pricing.discountPercentage,
      is_available: hotelInfo.hotelDetail.isAvailable,
      price_engine_rules: [{
        id: hotelInfo?.ruleId || 0,
        title: ""
      }],
      product: mapProductForUserTracking(process.env.NEXT_PUBLIC_APP_NAME as ApplicationNameType)
    });
    const listing_card_click_payload = {
      accommodation_id: hotelInfo.id,
      click_area: isImageClick ? "image_click" : isTitleClick ? "title_click" : isPricingClick ? "price_click" : undefined,
      position: index
    };
    if (router?.pathname?.includes("map") && process.env.NEXT_PUBLIC_APP_NAME === "villa") {
      addEventToTracking(USER_TRACKING_EVENTS.MAP_LISTING_CARD_CLICKED, {
        ...listing_card_click_payload,
        product: mapProductForUserTracking(process.env.NEXT_PUBLIC_APP_NAME as ApplicationNameType)
      });
    }
    if (process.env.NEXT_PUBLIC_APP_NAME === "tour") {
      onLinkClick?.();
    }
    if (["website", "homepage"].includes(process.env.NEXT_PUBLIC_APP_NAME as string)) {
      setClickLoading(0);
    }
    router[router.query.modal ? "replace" : "push"](hotelInfo?.hotelDetail?.hotelPageUrl?.href || {
      pathname: hotelInfo?.hotelDetail?.hotelPageUrl?.pathname,
      query: {
        ...omit(router.query, ["modal", "selectedHotelId"]),
        ...(hotelInfo?.hotelDetail?.hotelPageUrl?.query as object),
        ...query
      },
      hash
    }).finally(() => setClickLoading(0));
  };
};
export const HotelCardLink: React.FC<HotelCardLinkProperties> = properties => {
  const $ = _c(28);
  const router = useCompatRouter();
  if (properties.shouldRedirectUsingJs) {
    let t0;
    if ($[0] !== properties) {
      t0 = omit(properties, "href", "prefetch", "replace", "role", "target", "title", "noFollow");
      $[0] = properties;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    let t1;
    if ($[2] !== properties.children || $[3] !== t0) {
      t1 = <div {...t0}>{properties.children}</div>;
      $[2] = properties.children;
      $[3] = t0;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  }
  const t0 = properties.className;
  let t1;
  if ($[5] !== properties.href || $[6] !== properties.pathname || $[7] !== properties.query) {
    t1 = properties?.href || {
      pathname: properties.pathname,
      query: properties.query
    };
    $[5] = properties.href;
    $[6] = properties.pathname;
    $[7] = properties.query;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const t2 = properties.prefetch ?? true;
  const t3 = properties?.replace;
  const t4 = properties.noFollow ? "nofollow" : undefined;
  const t5 = properties?.target;
  const t6 = properties?.title;
  const t7 = properties?.utm;
  const t8 = properties?.setClickLoading;
  const t9 = properties?.onLinkClick;
  let t10;
  if ($[9] !== properties.homePageSectionName || $[10] !== properties.hotelInfo || $[11] !== properties.index || $[12] !== properties.isLastMinute || $[13] !== router || $[14] !== t7 || $[15] !== t8 || $[16] !== t9) {
    t10 = hotelCardOnClick({
      query: t7,
      router,
      setClickLoading: t8,
      onLinkClick: t9,
      index: properties.index,
      hotelInfo: properties.hotelInfo,
      homePageSectionName: properties.homePageSectionName,
      isLastMinute: properties.isLastMinute
    });
    $[9] = properties.homePageSectionName;
    $[10] = properties.hotelInfo;
    $[11] = properties.index;
    $[12] = properties.isLastMinute;
    $[13] = router;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== properties.children || $[19] !== properties.className || $[20] !== t1 || $[21] !== t10 || $[22] !== t2 || $[23] !== t3 || $[24] !== t4 || $[25] !== t5 || $[26] !== t6) {
    t11 = <Link className={t0} href={t1} prefetch={t2} replace={t3} rel={t4} role="link" target={t5} title={t6} onClick={t10}>{properties.children}</Link>;
    $[18] = properties.children;
    $[19] = properties.className;
    $[20] = t1;
    $[21] = t10;
    $[22] = t2;
    $[23] = t3;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
};